import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Text,
  Flex,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Icon,
  Button,
  Badge,
} from '@kognitos/lattice';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { useRenameAutomationTitle } from '@/shared/hooks/use-rename-automation-title';
import { formatTimestamp } from '@/shared/utils/datetime-utils';
import { getRoutePath } from '@/routes/util';
import { V1AutomationActivationState } from '@/generated/hooks/grimoire/models/v1AutomationActivationState';
import { DeactivatedBadge } from '@/shared/components/DeactivatedBadge';
import { AutomationStatusDot } from '@/shared/components/AutomationStatusDot';
import { EditableTitle } from '@/shared/components/EditableTitle';
import { AopMapViewToggle } from '@/shared/components/AopMapViewToggle';

/** Tooltip delay in milliseconds for the title tooltip */
const TITLE_TOOLTIP_DELAY_MS = 200;

/**
 * Props for the AutomationHeaderTitle component.
 */
interface IAutomationHeaderTitleProps {
  /** The automation display name */
  title?: string;
  /** The automation/draft ID */
  automationId?: string;
  /** Last updated timestamp (shown only for editable titles) */
  updatedAt?: string;
  /** Whether this is a draft */
  isDraft?: boolean;
  /** Whether the title supports inline renaming */
  isTitleEditable?: boolean;
  /**
   * Renders the automation's name, status dot and badges beside the back
   * button. The run page turns this off: the document card's own title bar
   * carries the name there, and two headings for one document on one screen
   * read as two documents. The back button is unaffected — the header is
   * still the only way back from a run.
   */
  showTitle?: boolean;
  /** Activation state of the automation */
  activationState?: V1AutomationActivationState;
  /** Optional callback to override back navigation (e.g., run page navigates to editor) */
  onBack?: () => void;
  /**
   * Opens the whole-automation test workbench. Rendered as a flask beside the
   * title — the automation-level counterpart to the per-step test button in the
   * runbook. Omit it to hide the affordance entirely (e.g. flag off).
   */
  onOpenWorkbench?: () => void;
  /**
   * Switches between the document and the AOP map. Omit it to hide the
   * affordance — a surface whose document carries its own
   * `AutomationDocumentTitleBar` puts the toggle there instead.
   */
  onToggleMapView?: () => void;
  /** Whether the map is showing, which points the toggle back at the document. */
  isMapView?: boolean;
}

/**
 * Title area for the automation page header: back button, status dot,
 * inline-editable title and last-saved timestamp.
 *
 * The draft document's own sticky title bar is a separate component —
 * `AutomationDocumentTitleBar` — because it carries different chrome, a stage
 * badge and its own options menu.
 *
 * @param {IAutomationHeaderTitleProps} props - Component props
 * @returns {JSX.Element} Title area component
 */
export function AutomationHeaderTitle({
  title,
  automationId,
  updatedAt,
  isDraft = true,
  isTitleEditable = false,
  showTitle = true,
  activationState,
  onBack,
  onOpenWorkbench,
  onToggleMapView,
  isMapView = false,
}: IAutomationHeaderTitleProps) {
  const { t } = useTranslation('drafts');
  const { t: tAutomation } = useTranslation('automation');
  const navigate = useNavigate();
  const location = useLocation();
  const shouldFocusTitle = Boolean(
    isTitleEditable &&
      (location.state as { focusTitle?: boolean } | null)?.focusTitle,
  );

  // Clear navigation state so back/forward navigation doesn't re-trigger focus
  if (shouldFocusTitle) {
    window.history.replaceState({}, '');
  }

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Shared with the document title bar so a rename from either place
  // invalidates the same caches and reports the same failure.
  const { rename: handleRename, isRenaming } =
    useRenameAutomationTitle(automationId);

  const navigateBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (isDraft) {
      navigate(
        getRoutePath('DRAFTS', {
          orgId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
        }),
      );
    } else if (automationId) {
      navigate(
        getRoutePath('AUTOMATION_DETAILS', {
          orgId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          automationId,
        }),
      );
    }
  };

  // The default draft back action navigates to all drafts; label it as such.
  // When a parent supplies onBack (e.g. run page → editor) keep the generic "Back".
  const backTooltip =
    isDraft && !onBack
      ? tAutomation('automation.header.tooltip.backToDrafts')
      : tAutomation('automation.header.tooltip.back');

  const isDeactivated =
    activationState ===
    V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED;

  const workbenchButton = onOpenWorkbench && (
    <Tooltip delayDuration={TITLE_TOOLTIP_DELAY_MS} variant="primary">
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          data-testid="test-automation-workbench-button"
          aria-label={tAutomation('automation.header.tooltip.testAutomation')}
          onClick={onOpenWorkbench}
        >
          <Icon type="FlaskConical" size="sm" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {tAutomation('automation.header.tooltip.testAutomation')}
      </TooltipContent>
    </Tooltip>
  );

  const mapToggleButton = onToggleMapView && (
    <AopMapViewToggle onToggle={onToggleMapView} isMapView={isMapView} />
  );

  const titleField = (
    <EditableTitle
      value={title ?? ''}
      onRename={handleRename}
      isEditable={isTitleEditable}
      isDisabled={isRenaming}
      autoFocusEdit={shouldFocusTitle}
      level="base"
      weight="medium"
      className="max-w-[300px]"
      inputName="automationTitle"
      inputId="automation-title"
      textTestId="automation-title-text"
    />
  );

  return (
    <Flex
      flex="1"
      className="min-w-0 mr-4"
      justify="start"
      align="center"
      gap={4}
    >
      <Flex align="center" gap={1}>
        <Tooltip delayDuration={TITLE_TOOLTIP_DELAY_MS} variant="primary">
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              onClick={navigateBack}
              aria-label={backTooltip}
            >
              <Icon type="ArrowLeft" size="sm" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{backTooltip}</TooltipContent>
        </Tooltip>

        {showTitle && (
          <>
            <AutomationStatusDot
              isDraft={isDraft}
              isDeactivated={isDeactivated}
            />

            {titleField}

            {!isDraft && (
              <Badge variant="success" data-testid="published-badge">
                {tAutomation('automation.header.badge.published')}
              </Badge>
            )}

            {isDeactivated && <DeactivatedBadge />}
          </>
        )}

        {/* Test the automation end to end. Sits with the title, not with the
            run/publish actions, because it is about THIS automation rather than
            an action on the current page. */}
        {workbenchButton}
        {mapToggleButton}
      </Flex>

      {showTitle && updatedAt && isTitleEditable && (
        <Text data-testid="last-saved-at" color="muted" className="text-xs">
          {t('drafts.list.lastSavedAt', {
            time: formatTimestamp(updatedAt, 'h:mm A'),
          })}
        </Text>
      )}
    </Flex>
  );
}
